import {Observable} from 'rxjs';
import {
    APIResponse,
    DataTableColumn,
    Filter,
    FilterFieldOptionRequest,
    FilterFieldOptionResponse,
    GenericListRequest,
    PagingResponse,
    PresignedURL,
    Simple2DMultiSeries,
    SimpleMultiSeries,
    SimpleSeries,
    TableFilterConfig,
    YearQuarterPair,
} from '@core/interfaces/system/system-common';
import {DataSource} from '@mominsamir/ngx-smart-table/lib/lib/data-source/data-source';
import {DownloadCSVUrl} from '@core/interfaces/engin/workflow-validation';
import {
    AssetInspectionResultDto,
    InspectionResultFullDto,
} from '@core/interfaces/engin/maintenance-planning/form-visualization';
import {NbComponentStatus} from '@nebular/theme';
import {AssetColumn} from '@core/interfaces/common/asset';
import {
    ActivityBulkUpdateDto,
    ActivityChangeStatus,
    ActivityCreateDto,
    ActivityDto,
    ActivityUpdateDto,
    CsvColumnRequest,
    DeficiencyBulkUpdateDto,
    DeficiencyChangeStatus,
    DeficiencyCreateDto,
    DeficiencyDto,
    DeficiencyFullDto,
    DeficiencyUpdateDto,
    InspectionStatsDto,
    LinkActivitiesToDeficiency,
} from '@core/interfaces/engin/maintenance-planning/deficiencies.model';

export enum PageType {
    VIEW_LATEST = 'VIEW_LATEST',
    VIEW = 'VIEW',
    REVIEW = 'REVIEW',
    SUBMIT_CORRECTION = 'SUBMIT_CORRECTION',
}
export enum ValueOptionType {
    NUMERIC_RANGE = 'NUMERIC_RANGE',
    SINGLE_SELECT = 'SINGLE_SELECT',
    MULTI_SELECT = 'MULTI_SELECT',
    FREE_TEXT = 'FREE_TEXT',
    CHECKBOX = 'CHECKBOX',
    IMAGE = 'IMAGE',
    VIDEO = 'VIDEO',
    IMAGE_VIDEO = 'IMAGE_VIDEO',
    SIGNATURE = 'SIGNATURE',
    ISSUES = 'ISSUES',
    DATE_RANGE = 'DATE_RANGE',
    TABLE = 'TABLE',
    BOOLEAN = 'BOOLEAN',
    DEFAULT = 'DEFAULT',
}
export interface InspectionResultDto {
    id: number;
    inspectionResultCode: string;
    inspectedDate: string;
    inspectedById: string;
    inspectedByName: string;
    status: InspectionStatusType;
    comment: string;
    approvedDate: string;
    approvedById: string;
    approvedByName: string;
    assetId: string;
    formId: number;
    formName: string;
    formVersion: number;
    latestFormVersion: number;
    groupName?: string;
}

export interface InspectionSummaryStatsDto {
    pendingReviewCount: number;
    summaryStats: SimpleSeries<string, number>;
}

export interface InspectionAssetResultDto {
    id: number;
    inspectionResultCode: string;
    inspectedDate: string;
    inspectedByName: string;
    status: InspectionStatusType;
    comment: string;
    approvedDate: string;
    approvedByName: string;
    formName: string;
    formVersion: number;
    assetFormId: number;
    assetId: string;
    groupId: number;
    groupName: string;
}

export interface ChangeHistoryDto {
    changeDate: string; // Using string to represent LocalDateTime
    changeDoneByName: string;
    rows: string[];
}

export interface InspectionResultReviewDto {
    status: InspectionStatusType;
    comment: string;
    changeDoneById: string;
    changeDoneByName: string;
    assetInspectionResult: AssetInspectionResultDto;
}

export interface InspectionResultCorrectionDto {
    comment: string;
    changeDoneById: string;
    changeDoneByName: string;
    assetInspectionResult: AssetInspectionResultDto;
}

export enum InspectionStatusType {
    PENDING_REVIEW = 'PENDING_REVIEW',
    REJECTED = 'REJECTED',
    APPROVED = 'APPROVED',
    CORRECTED = 'CORRECTED',
}
export const InspectionStatusEnumLabel = {
    [InspectionStatusType.PENDING_REVIEW]: 'Pending Review',
    [InspectionStatusType.REJECTED]: 'Rejected',
    [InspectionStatusType.APPROVED]: 'Approved',
    [InspectionStatusType.CORRECTED]: 'Corrected',
};

export enum ButtonType {
    START_REVIEW = 'START_REVIEW',
    MAKE_A_CORRECTION = 'MAKE_A_CORRECTION',
    REJECT_WITH_COMMENT = 'REJECT_WITH_COMMENT',
    CHANGE_HISTORY = 'CHANGE_HISTORY',
    APPROVE = 'APPROVE',
    CLOSE = 'CLOSE',
    CANCEL = 'CANCEL',
    SUBMIT = 'SUBMIT',
    DEFICIENCIES = 'DEFICIENCIES',
    VISIBILITY = 'VISIBILITY',
    DELETE = 'DELETE',
}

export enum FormMode {
    VIEW = 'VIEW',
    EDIT = 'EDIT',
}

export interface PageHeaderSetting {
    pageTitle: string;
    pageHeaderButton: PageHeaderButton[];
}
export interface PageHeaderButton {
    title: string;
    code: ButtonType;
    status: NbComponentStatus;
    display: boolean;
    alwaysShow?: boolean;
}

export interface InspectionFieldRequest {
    fieldValue: string;
    fieldCount: number;
}

export interface InspectionChartComparisonRequest extends GenericListRequest {
    grouping: AssetColumn;
}

export interface ActivityBarChartDataDto {
    status: string;
    value: number;
}

export enum MaintenancePageEnum {
    MaintenanceList = 'maintenance-list',
    MaintenanceDashboard = 'maintenance-dashboard',
}

export enum AssetRegistryEnum {
    ViewPageType = 'VIEW',
    ReViewPageType = 'REVIEW',
    SubmitCorrectionPageType = 'SUBMIT_CORRECTION',
}

export interface InspectionForm {
    completed: number;
    total: number;
    invalid: number;
}

export interface SelectedPath {
    sectionId: number;
    subsectionId: number;
}

export abstract class MaintenancePlanningService {
    // Inspection Details
    abstract findOneDto(resultId: number): Observable<InspectionResultDto>;

    abstract findOneFullDto(resultId: number): Observable<InspectionResultFullDto>;

    abstract getChangeHistory(resultId: number): Observable<ChangeHistoryDto[]>;

    abstract submitReview(resultId: number, req: InspectionResultReviewDto): Observable<any>;

    abstract submitCorrection(
        resultId: number,
        req: InspectionResultCorrectionDto,
    ): Observable<InspectionResultFullDto>;

    abstract findLatestInspectionResult(resultId: number): Observable<APIResponse<InspectionResultDto>>;

    abstract getPresignedUrl(fileName: string, fileFormat: string): Observable<APIResponse<PresignedURL>>;

    abstract uploadToPresignedUrl(url: string, data: File);

    // Options
    abstract getFilterOptions(): Observable<APIResponse<TableFilterConfig[]>>;

    abstract getFilterFieldOptions(req: FilterFieldOptionRequest): Observable<APIResponse<FilterFieldOptionResponse>>;

    abstract getFilterFieldOptionsTable(
        fieldKey: string,
        searchString: string,
    ): Observable<APIResponse<FilterFieldOptionResponse>>;

    // Inspection List
    abstract listDto(): Observable<DataSource>; //data: InspectionResultDto[]

    abstract listDtoCsv(paramsString?: string): Observable<APIResponse<DownloadCSVUrl>>;

    // Inspection Dashboard
    abstract getGroupOptions(): Observable<APIResponse<AssetColumn[]>>;

    abstract listInspectionAssetDto(req: GenericListRequest): Observable<DataSource>; //InspectionAssetResultDto[]

    abstract getListInspectionAssetDtoCsv(paramsString: string, filterParams: Filter[]): Observable<DownloadCSVUrl>;

    abstract getInspectionsByStatus(req: GenericListRequest): Observable<APIResponse<SimpleSeries<string, number>>>;

    abstract getInspectionsComparison(
        req: InspectionChartComparisonRequest,
    ): Observable<APIResponse<SimpleMultiSeries<string>>>;

    // Asset Registry > Maintenance
    abstract getInspectionsTimelineByParentAssetId(
        parentAssetId: string,
    ): Observable<APIResponse<SimpleSeries<YearQuarterPair, number>>>;

    abstract getInspectionsSummaryByParentAssetId(
        parentAssetId: string,
    ): Observable<APIResponse<InspectionSummaryStatsDto>>;

    abstract listDtoByParentAssetId(parentAssetId: string): Observable<DataSource>;
    abstract listDtoCsvByParentAssetId(
        parentAssetId: string,
        paramString?: string,
    ): Observable<APIResponse<DownloadCSVUrl>>;

    // Deficiencies and Activities
    abstract getDeficienciesDtoList(filterString?: string): Observable<DataSource>; //items: DeficiencyDto[]
    abstract getDeficienciesListByParentId(parentAssetId: string, filterString?: string): Observable<DataSource>; //items: DeficiencyDto[]
    abstract getDeficienciesByInspectionCode(inspectionResultCode: string): Observable<PagingResponse<DeficiencyDto>>; //items: DeficiencyDto[]
    abstract downloadDeficienciesList(req: CsvColumnRequest, paramsString?: string): Observable<DownloadCSVUrl>;
    abstract downloadDeficienciesListByParentId(
        parentAssetId: string,
        req: CsvColumnRequest,
        paramsString?: string,
    ): Observable<DownloadCSVUrl>;
    abstract updateDeficiencyStatus(statusChangeRequest: DeficiencyChangeStatus): Observable<any>;
    abstract createDeficiency(deficiency: DeficiencyCreateDto): Observable<any>;
    abstract updateDeficiency(id: number, deficiency: DeficiencyUpdateDto): Observable<any>;
    abstract getDeficiencyFullDto(id: number): Observable<DeficiencyFullDto>;
    abstract getDeficienciesCategories(searchString: string): Observable<string[]>;
    abstract getDeficienciesColumns(): Observable<DataTableColumn[]>;
    abstract getDeficienciesFilterFieldOptionsTable(
        fieldKey: string,
        searchString: string,
        filterParams?: Filter[],
    ): Observable<APIResponse<FilterFieldOptionResponse>>;
    abstract getDeficienciesFilterFieldOptionsTableByParentId(
        parentAssetId,
        fieldKey: string,
        searchString: string,
        filterParams?: Filter[],
    ): Observable<APIResponse<FilterFieldOptionResponse>>;
    abstract bulkUpdateDeficiency(req: DeficiencyBulkUpdateDto): Observable<any>;
    abstract getDeficienciesTimelineByAsset(
        assetId: string,
    ): Observable<APIResponse<SimpleSeries<YearQuarterPair, number>>>;
    abstract getDeficienciesBarChartByAsset(assetId: string): Observable<APIResponse<SimpleSeries<string, number>>>;

    abstract getActivityTimelineByAsset(
        assetId: string,
    ): Observable<APIResponse<Simple2DMultiSeries<YearQuarterPair, string>>>;

    abstract getActivityBarChartByAsset(
        assetId: string,
    ): Observable<APIResponse<SimpleSeries<string, ActivityBarChartDataDto>>>;

    abstract getActivitiesDtoList(filterString?: string): Observable<DataSource>; //items: ActivityDto[]
    abstract getActivitiesListByParentId(parentAssetId: string, filterString?: string): Observable<DataSource>; //items: ActivityDto[]
    abstract getActivitiesList(filterString?: string): Observable<PagingResponse<ActivityDto>>;
    abstract downloadActivitiesList(req: CsvColumnRequest, paramsString?: string): Observable<DownloadCSVUrl>;
    abstract downloadActivitiesListByParentId(
        parentAssetId: string,
        req: CsvColumnRequest,
        paramsString?: string,
    ): Observable<DownloadCSVUrl>;
    abstract createActivity(activity: ActivityCreateDto): Observable<APIResponse<ActivityDto>>;
    abstract updateActivityStatus(statusChangeRequest: ActivityChangeStatus): Observable<any>;
    abstract getActivityFullDto(id: number): Observable<ActivityDto>;
    abstract getActivityCategories(searchString: string): Observable<string[]>;
    abstract updateActivity(id: number, activity: ActivityUpdateDto): Observable<any>;
    abstract getInspectionStats(id: number): Observable<InspectionStatsDto>;
    abstract getActivitiesColumns(): Observable<DataTableColumn[]>;
    abstract bulkLinkDeficiency(req: LinkActivitiesToDeficiency): Observable<any>;
    abstract getActivitiesFilterFieldOptionsTable(
        fieldKey: string,
        searchString: string,
        filterParams?: Filter[],
    ): Observable<APIResponse<FilterFieldOptionResponse>>;
    abstract getActivitiesFilterFieldOptionsTableByParentId(
        parentAssetId,
        fieldKey: string,
        searchString: string,
        filterParams?: Filter[],
    ): Observable<APIResponse<FilterFieldOptionResponse>>;
    abstract bulkUpdateActivity(req: ActivityBulkUpdateDto): Observable<any>;
    abstract scanForDeficiencies(inspectionIds: number[]): Observable<number>;
}
